import { Hash, Tag } from "lucide-react"
import { Badge } from "@/components/ui/Badge"
import StockStatus from "@/pages/Inventory/components/StockStatus"
import type { QuickViewData } from "./types"
import KeyNumbers from "./KeyNumbers"

interface Props {
  externalId: string
  data: QuickViewData | null
  ready: boolean
}

// A white section at the top of the panel's scrolling area, above the cards:
// the full name; one line with the product id, short name (when set), product
// group (id and group each behind an icon) and the Inventory Overview Status;
// then the three key numbers once loaded. Until the product loads, its
// external id stands in for the name.
export default function PanelHeader({ externalId, data, ready }: Props) {
  const product = data?.product

  return (
    <div className="flex flex-col gap-3 border-b border-border bg-card px-5 py-4">
      <div className="flex flex-col gap-2">
        <h2 className="min-w-0 break-words text-lg font-semibold text-foreground">{product?.name ?? externalId}</h2>
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-foreground">
          <span className="flex items-center gap-0.5">
            <Hash size={12} aria-label="Product ID" className="shrink-0" />
            <span className="select-all" title="Product ID">{product?.external_id ?? externalId}</span>
          </span>
          {product?.short_name && <span title="Short name">{product.short_name}</span>}
          {product && (
            <span className="flex items-center gap-0.5">
              <Tag size={12} aria-label="Group" className="shrink-0" />
              <span title="Group">Group: {product.product_group ?? "Unassigned"}</span>
            </span>
          )}
          {data && <StockStatus row={data.stock_status} className="flex items-center gap-1" />}
          {product?.discontinued && <Badge variant="outline">Discontinued</Badge>}
        </div>
      </div>
      {ready && data && <KeyNumbers data={data} />}
    </div>
  )
}
